import React, { useRef, useEffect } from 'react'
import { Check, ChevronsRight, Flag, Lock } from 'lucide-react'
import { format, isValid } from 'date-fns';
import { Link } from 'react-router-dom';  
import { Skeleton } from '../../../components/ui/skeleton';
import { useTranslation } from 'react-i18next';
import { getTranslationString } from '../../../lib/translation';

type Props = {
  pronos?: Prono[]
  userResults?: UserPronoResult[]
}

const PronoCard = ({prono, userResult}:{prono?:Prono, userResult?:UserPronoResult}) => {
  const {t, i18n} = useTranslation()

  const picked = userResult

  return (
    <Link to={`${prono?.m_iPronoID}`}>
      <div
        className={`${(prono?.m_bClosed) ? 'bg-neutral-200 grayscale' : 'bg-tifo'} h-96 snap-center shrink-0 p-4 w-64 rounded-md relative flex items-end group`}
      > 
        <div className={`${!prono?.m_bActive ? 'bg-neutral-200 text-neutral-700' : 'bg-tifo-light text-tifo'} shadow-sm absolute z-10 top-0 left-1/2 -translate-x-1/2 -translate-y-1/2 font-semibold px-3 py-1`}>
          {isValid(new Date(prono?.m_dtStart)) ? format(new Date(prono?.m_dtStart), "d MMM yyyy") : t("event.o_slider.unknown")}
        </div>

        {(prono?.m_bClosed) && 
        <div className='absolute text-white z-[2] bg-gradient-to-b from-black to-transparent w-full py-10 font-semibold rounded-t-md flex justify-center items-center text-sm uppercase left-0 top-0'>
          <p className='flex items-center gap-2 duration-200 -translate-x-4 group-hover:translate-x-0'><Flag className='w-4 duration-500 opacity-0 group-hover:fill-white group-hover:opacity-100 fill-transparent'/>{t("event.o_slider.view_results")}</p>
        </div>
        }

        {(picked && !prono?.m_bClosed) &&
        <div className='absolute text-white z-[2] bg-gradient-to-b from-black to-transparent w-full py-10 font-semibold rounded-t-md flex justify-center items-center text-sm uppercase left-0 top-0'>
          <p className='flex items-center gap-2 duration-200'><Check className='w-4 fill-transparent'/>{t("event.o_slider.filled_in")}</p>
        </div>
        }

        <div className='absolute left-0 top-0 w-full h-full bg-black/50 z-[1] rounded-md duration-300 backdrop-blur-sm hover:backdrop-blur-none'></div>
        <img className='absolute top-0 left-0 object-cover h-full rounded-md' src={prono?.m_sImageUrl ? prono?.m_sImageUrl : 'https://images.pexels.com/photos/248547/pexels-photo-248547.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=1'} alt={`${prono?.m_oPronoType.m_sDescriptionNL} ${prono?.m_sDescriptionNL}`} />

        <button className='w-10 h-10 bg-white/20 rounded-full z-[1] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white flex justify-center items-center hover:bg-white/40'>
          {(prono?.m_bClosed)
            ? <Lock className='w-5'/>
            : <ChevronsRight className='w-5'/>
          }
          
        </button>

        <div className={`${prono?.m_bClosed && 'opacity-50'} z-[1]`}>
          <p className='text-2xl font-bold text-white uppercase'>{getTranslationString(i18n.language.toUpperCase() as Language, prono?.m_oPronoType, "m_sNameNL") || prono?.m_oPronoType.m_sNameNL}</p>
          <p className='text-white uppercase line-clamp-4'>{getTranslationString(i18n.language.toUpperCase() as Language, prono?.m_oPronoType, "m_sDescriptionNL") || prono?.m_sDescriptionNL}</p>
        </div>          
      </div>
    </Link>
  )
}

const Slider : React.FC<Props> = ({pronos, userResults}) => {
    const scrollContainerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        // Function to calculate the card with the closest date to today
        if(!pronos) { return; }
        const findClosestDate = () => {
          const today = new Date();
          let closestDateIndex = 0;
          let closestDateDiff = Math.abs(today.getTime() - new Date(pronos[0].m_dtStart).getTime());
    
          pronos.forEach((prono, index) => {
            const dateDiff = Math.abs(today.getTime() - new Date(prono.m_dtStart).getTime());
            if (dateDiff < closestDateDiff) {
              closestDateDiff = dateDiff;
              closestDateIndex = index;
            }
          });
    
          return closestDateIndex;
        };
    
        // Scroll to the card with the closest date on initial render
        const closestDateIndex = findClosestDate();
        const closestCard = scrollContainerRef.current?.children[closestDateIndex] as HTMLDivElement;
        if (closestCard) {
          const containerWidth = scrollContainerRef.current?.offsetWidth || 0;
          const cardWidth = closestCard.offsetWidth;
          let scrollOffset = closestCard.offsetLeft - (containerWidth - cardWidth) / 2;


          const amountChildren = scrollContainerRef.current?.children.length || 0;
          const amountGap = amountChildren * 24

          scrollOffset += amountGap

          scrollContainerRef.current?.scrollTo({ left: scrollOffset, behavior: 'smooth' });
        }
    }, [pronos]);

    return (
        <div
            ref={scrollContainerRef}
          className={`${(pronos && pronos.length) > 5 ? 'max-w-7xl' : 'max-w-5xl'} relative w-full mx-auto flex gap-6 snap-x overflow-x-auto py-4 px-4 sm:px-0`}
        >
          {pronos && pronos.map((prono, index) => {
            const userResult = userResults?.find(i=>i.m_iPronoID === prono.m_iPronoID)
            return (
              <PronoCard prono={prono} userResult={userResult} key={index}/>
            )
          })}
          {!pronos && Array(5).fill('').map((_,i)=>(
            <Skeleton key={i} className={`h-96 bg-neutral-200 snap-center shrink-0 p-4 w-64 rounded-md relative flex items-end group`}></Skeleton>
          ))}
        </div>
    );
}

export default Slider